import type React from 'react';
import {useState} from 'react';
import {Box, Button, IconButton, Link, Stack, Tooltip, Typography} from '@mui/material';
import {Add, VisibilityOffOutlined} from '@mui/icons-material';
import {Tag} from '~/components/tag/Tag';
import {OVERVIEW_HELP} from '../../domain/overviewHelp';
import type {RecordActions} from '../../hooks/useRecordActions';
import type {CategoryRecord, CategoryRisk, RiskDraft} from '../../types/categoryRecordTypes';
import {ProfileSection} from '../components/ProfileSection';
import {RowAction} from '../components/RowAction';
import {RiskDialog} from './RiskDialog';

type Props = {record: CategoryRecord; actions: RecordActions; flags: string[]};

/** A risk on screen: a stored one, or a detected flag nobody has edited yet (`id` null). */
type Row = RiskDraft & {id: number | null; source: string | null; status: string; stored: CategoryRisk | null};

const LEVEL_COLOR = {High: 'error', Medium: 'warning', Low: 'default'} as const;
const STATUS_COLOR = {Open: 'info', Mitigated: 'success', Closed: 'default'} as const;
const EMPTY = {title: '', description: '', mitigation: '', likelihood: 'Medium', impact: 'Medium', status: 'Open'};

/**
 * Category risks (Overview): risks detected from the data (the header's risk flags), each with a drafted mitigation,
 * and the risks the team adds. Editing a detected risk stores the team's version; dismissing hides it (with Undo).
 */
export const CategoryRisksSection: React.FC<Props> = ({record, actions, flags}) => {
    const [editing, setEditing] = useState<Row | 'new' | null>(null);
    const {riskFlags, riskLevels, riskStatuses} = record.options;
    const bySource = new Map(record.risks.filter(r => r.source).map(r => [r.source!, r]));
    const detected: Row[] = flags.filter(f => riskFlags[f]).map(f => {
        const stored = bySource.get(f) ?? null;
        return stored ? {...stored, stored} : {...riskFlags[f], id: null, source: f, status: 'Open', stored: null};
    });
    const rows = [...detected.filter(r => !r.stored?.dismissed),
        ...record.risks.filter(r => !r.source).map(r => ({...r, stored: r}))];
    const dismissed = detected.filter(r => r.stored?.dismissed);

    const save = (row: Row | 'new', input: Parameters<typeof actions.addRisk>[0]) => {
        if (row === 'new') return actions.addRisk(input);
        if (row.stored) return actions.updateRisk(row.stored, input);
        return actions.addRisk({...input, source: row.source!}, 'Risk updated.');
    };
    const dismiss = (row: Row) => row.stored
        ? actions.updateRisk(row.stored, {dismissed: true}, `${row.title} dismissed.`)
        : actions.addRisk({source: row.source!, dismissed: true}, `${row.title} dismissed.`);

    return (
        <ProfileSection
            title={`Category risks (${rows.length})`}
            help={OVERVIEW_HELP.risks}
            actions={record.canEdit && <Button size="small" startIcon={<Add/>} onClick={() => setEditing('new')}>Add risk</Button>}
        >
            {rows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No risks detected or recorded.{record.canEdit ? ' Add the risks the data cannot see.' : ''}
                </Typography>
            ) : (
                <Stack divider={<Box sx={{borderTop: 1, borderColor: 'divider'}}/>}>
                    {rows.map(r => (
                        <Stack key={r.id ?? r.source} direction="row" gap={1.5} alignItems="flex-start" sx={{py: 1.25}}>
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                    <Typography variant="body2" sx={{fontWeight: 600}}>{r.title}</Typography>
                                    {r.source && <Tag size="small" solid={false} color="default" label="Detected"
                                                      title="Detected from the data (the badge next to the category name)"/>}
                                    <Tag size="small" solid={false} color={LEVEL_COLOR[r.likelihood as keyof typeof LEVEL_COLOR] ?? 'default'}
                                         label={`Likelihood: ${r.likelihood}`}/>
                                    <Tag size="small" solid={false} color={LEVEL_COLOR[r.impact as keyof typeof LEVEL_COLOR] ?? 'default'}
                                         label={`Impact: ${r.impact}`}/>
                                    <Tag size="small" color={STATUS_COLOR[r.status as keyof typeof STATUS_COLOR] ?? 'default'} label={r.status}/>
                                </Stack>
                                {r.description && <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>{r.description}</Typography>}
                                {r.mitigation && (
                                    <Typography variant="body2" sx={{mt: 0.5}}>
                                        <Box component="span" sx={{color: 'text.secondary'}}>Mitigation: </Box>{r.mitigation}
                                    </Typography>
                                )}
                            </Box>
                            {record.canEdit && (
                                <Stack direction="row">
                                    <RowAction kind="edit" label={`risk ${r.title}`} onClick={() => setEditing(r)}/>
                                    {r.source ? (
                                        <Tooltip title={`Dismiss ${r.title}: it does not apply`}>
                                            <IconButton size="small" aria-label={`Dismiss ${r.title}`}
                                                        onClick={() => void dismiss(r).catch(() => undefined)}>
                                                <VisibilityOffOutlined fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <RowAction kind="delete" label={`risk ${r.title}`}
                                                   onClick={() => void actions.removeRisk(r.stored!).catch(() => undefined)}/>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    ))}
                </Stack>
            )}
            {dismissed.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                    {'Dismissed: '}
                    {dismissed.map((r, i) => (
                        <span key={r.source}>
                            {i > 0 && ', '}
                            {r.title}
                            {record.canEdit && (
                                <>
                                    {' ('}
                                    <Link component="button" type="button" variant="caption"
                                          onClick={() => void actions.updateRisk(r.stored!, {dismissed: false}, `${r.title} restored.`)
                                              .catch(() => undefined)}>
                                        restore
                                    </Link>
                                    {')'}
                                </>
                            )}
                        </span>
                    ))}
                </Typography>
            )}
            {editing && (
                <RiskDialog
                    title={editing === 'new' ? 'Add risk' : `Edit risk${editing.source ? ' (detected)' : ''}`}
                    initial={editing === 'new' ? EMPTY : {title: editing.title, description: editing.description,
                        mitigation: editing.mitigation, likelihood: editing.likelihood, impact: editing.impact, status: editing.status}}
                    levels={riskLevels} statuses={riskStatuses}
                    onSubmit={input => save(editing, input)}
                    onClose={() => setEditing(null)}
                />
            )}
        </ProfileSection>
    );
};
